import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { ArrowLeft, Calendar, Database, FileText, Loader2, Play, ShieldAlert, ListChecks, Wrench } from 'lucide-react';
import { toast } from 'sonner';
import { getFeatureTimeline, aliasBackfillDryRun, aliasBackfillApply } from '../lib/api';

const categoryColor = {
  'dispatches': 'bg-blue-100 text-blue-800',
  'compliance': 'bg-rose-100 text-rose-800',
  'backups': 'bg-violet-100 text-violet-800',
  'infrastructure': 'bg-zinc-100 text-zinc-800',
  'data-tools': 'bg-amber-100 text-amber-800',
};

const typeIcon = {
  'feature': '✨',
  'fix': '🛠',
  'script': '📜',
};

export default function FeatureTimeline() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  // Alias backfill state
  const [backfillOpen, setBackfillOpen] = useState(false);
  const [backfillCustomerId, setBackfillCustomerId] = useState('');
  const [backfillReason, setBackfillReason] = useState('');
  const [dryRunResult, setDryRunResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getFeatureTimeline();
        setEntries(data.entries || []);
      } catch (e) {
        toast.error('Failed to load feature timeline');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = filter === 'all' ? entries : entries.filter(e => e.category === filter);
  const categories = Array.from(new Set(entries.map(e => e.category).filter(Boolean)));

  const runDryRun = async () => {
    setBusy(true);
    setDryRunResult(null);
    try {
      const { data } = await aliasBackfillDryRun(backfillCustomerId.trim() || null);
      setDryRunResult(data);
      if (data.would_touch_count === 0) {
        toast.info('No dispatches need backfilling — nothing to do');
      } else {
        toast.success(`Dry run: ${data.would_touch_count} dispatch(es) would be updated`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Dry run failed');
    } finally {
      setBusy(false);
    }
  };

  const runApply = async () => {
    if (!dryRunResult || dryRunResult.would_touch_count === 0) {
      toast.error('Run dry-run first and verify the candidates');
      return;
    }
    if (backfillReason.trim().length < 10) {
      toast.error('Reason must be ≥10 characters (audit requirement)');
      return;
    }
    setBusy(true);
    try {
      const { data } = await aliasBackfillApply(backfillCustomerId.trim() || null, backfillReason.trim());
      toast.success(`Backfill complete: ${data.touched_count} dispatch(es) updated`);
      setBackfillOpen(false);
      setDryRunResult(null);
      setBackfillReason('');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="feature-timeline-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/admin/data-management')}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to Data Management
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Calendar className="h-6 w-6" /> Feature & Script Timeline
          </h1>
          <p className="text-sm text-zinc-500">Chronological list of features, fixes, and one-shot scripts. Source-of-truth lives in git.</p>
        </div>
      </div>

      {/* ── Inline backfill tool card ── */}
      <Card className="border-amber-200 bg-amber-50/30">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="h-5 w-5 text-amber-600" />
            Available scripts
          </CardTitle>
          <CardDescription>One-shot, super-admin-only, fully audit-logged.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="bg-white rounded border p-3 flex items-start justify-between gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="font-medium">Alias name backfill on historical dispatches</div>
              <div className="text-xs text-zinc-600 mt-0.5">
                Copies <code>alias_name</code> from customer additional addresses into dispatches whose snapshot pre-dated the field.
                Idempotent · single field touched · audit-logged per dispatch · dry-run first.
              </div>
            </div>
            <Button size="sm" variant="outline" onClick={() => setBackfillOpen(true)} data-testid="btn-open-alias-backfill">
              <Play className="h-3 w-3 mr-1" /> Run
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ── Filter chips ── */}
      <div className="flex flex-wrap gap-2 items-center">
        <span className="text-sm text-zinc-600">Filter:</span>
        <button onClick={() => setFilter('all')} className={`text-xs px-2 py-1 rounded ${filter==='all' ? 'bg-zinc-900 text-white' : 'bg-zinc-100'}`}>All ({entries.length})</button>
        {categories.map(c => (
          <button key={c} onClick={() => setFilter(c)} className={`text-xs px-2 py-1 rounded ${filter===c ? 'bg-zinc-900 text-white' : (categoryColor[c] || 'bg-zinc-100')}`}>{c}</button>
        ))}
      </div>

      {/* ── Timeline ── */}
      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <div className="flex items-center justify-center p-12 text-zinc-500"><Loader2 className="h-4 w-4 animate-spin mr-2" /> Loading…</div>
          ) : filtered.length === 0 ? (
            <p className="text-center text-zinc-500 py-8">No entries yet for this filter.</p>
          ) : (
            <ol className="space-y-4">
              {filtered.map((e, i) => (
                <li key={`${e.date}-${i}`} className="border-l-2 border-zinc-300 pl-4 pb-2" data-testid={`tl-entry-${i}`}>
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-zinc-500">{e.date}</span>
                    <span className="text-lg leading-none">{typeIcon[e.type] || '•'}</span>
                    <span className="font-medium">{e.title}</span>
                    {e.category && (
                      <Badge variant="outline" className={`text-[10px] py-0 px-1.5 ${categoryColor[e.category] || ''}`}>{e.category}</Badge>
                    )}
                    {e.permission && (
                      <Badge variant="outline" className="text-[10px] py-0 px-1.5 text-rose-700">
                        <ShieldAlert className="h-2.5 w-2.5 mr-0.5" /> {e.permission}
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-zinc-700 mt-1">{e.description}</p>
                  {e.endpoint && <code className="text-[11px] text-zinc-500 block mt-1">→ {e.endpoint}</code>}
                  {e.files?.length > 0 && (
                    <details className="mt-1">
                      <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-700">Files ({e.files.length})</summary>
                      <ul className="text-[11px] font-mono text-zinc-600 mt-1 ml-2 space-y-0.5">
                        {e.files.map(f => <li key={f}>{f}</li>)}
                      </ul>
                    </details>
                  )}
                </li>
              ))}
            </ol>
          )}
        </CardContent>
      </Card>

      {/* ── Alias backfill dialog ── */}
      <Dialog open={backfillOpen} onOpenChange={(o) => { setBackfillOpen(o); if (!o) { setDryRunResult(null); setBackfillReason(''); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="h-5 w-5 text-amber-600" /> Alias name backfill
            </DialogTitle>
            <DialogDescription>
              Copies <code>alias_name</code> from each customer's saved addresses into matching historical dispatches.
              Step 1: dry-run to see what would change. Step 2: apply with a reason.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-2">
            <div>
              <label className="text-sm font-medium">Customer ID (optional)</label>
              <Input
                placeholder="Leave blank to backfill across ALL customers"
                value={backfillCustomerId}
                onChange={(e) => setBackfillCustomerId(e.target.value)}
                data-testid="input-backfill-customer-id"
              />
              <p className="text-xs text-zinc-500 mt-1">Get the UUID from Customers → Edit → URL or DB.</p>
            </div>

            <Button onClick={runDryRun} disabled={busy} variant="outline" data-testid="btn-dry-run-backfill">
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ListChecks className="h-4 w-4 mr-1" />}
              Run dry-run
            </Button>

            {dryRunResult && (
              <div className="bg-zinc-50 border rounded p-3 space-y-2">
                <div className="text-sm">
                  <strong>{dryRunResult.would_touch_count}</strong> dispatch(es) would receive an alias.
                  {dryRunResult.candidates_truncated && <em className="text-zinc-500"> (showing first 200)</em>}
                </div>
                {dryRunResult.candidates?.length > 0 && (
                  <div className="max-h-60 overflow-y-auto bg-white border rounded p-2">
                    <table className="w-full text-xs">
                      <thead className="text-zinc-500">
                        <tr><th className="text-left p-1">Dispatch</th><th className="text-left p-1">Customer</th><th className="text-left p-1">Address</th><th className="text-left p-1">New Alias</th></tr>
                      </thead>
                      <tbody>
                        {dryRunResult.candidates.map(c => (
                          <tr key={c.dispatch_id} className="border-t">
                            <td className="p-1 font-mono">#{c.dispatch_number}</td>
                            <td className="p-1">{c.customer_name}</td>
                            <td className="p-1 text-zinc-600">{c.address_label}</td>
                            <td className="p-1 font-medium">{c.new_alias_name}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {dryRunResult && dryRunResult.would_touch_count > 0 && (
              <div>
                <label className="text-sm font-medium">Reason (audit log, min 10 chars)</label>
                <Textarea
                  rows={2}
                  value={backfillReason}
                  onChange={(e) => setBackfillReason(e.target.value)}
                  placeholder="e.g. Lidl alias added 23-Jun — backfilling historical dispatches for consistency"
                  data-testid="input-backfill-reason"
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setBackfillOpen(false)} disabled={busy}>Close</Button>
            <Button
              onClick={runApply}
              disabled={busy || !dryRunResult || dryRunResult.would_touch_count === 0 || backfillReason.trim().length < 10}
              data-testid="btn-apply-backfill"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Database className="h-4 w-4 mr-1" />}
              Apply backfill
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
